import React, { useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { MessageSquareHeart, Loader2, Star, Send, Bug, Lightbulb, Heart } from 'lucide-react';

const TYPES = [
  { id: 'bug', label: 'Bug', icon: Bug },
  { id: 'idea', label: 'Idea', icon: Lightbulb },
  { id: 'praise', label: 'Praise', icon: Heart },
];

export const FeedbackDialog = ({ open, onOpenChange }) => {
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(0);
  const [type, setType] = useState('idea');
  const [allowFeature, setAllowFeature] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => { setMessage(''); setRating(0); setType('idea'); setAllowFeature(false); };

  const submit = async () => {
    if (message.trim().length < 3) { toast.error('Please share a little more.'); return; }
    setBusy(true);
    try {
      await api.post('/feedback', { message: message.trim(), rating: rating || null, type, allow_feature: allowFeature });
      toast.success('Thank you \u2014 your note is in. It genuinely helps.');
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send feedback');
    } finally {
      setBusy(false);
    }
  };

  const placeholders = {
    bug: 'What went wrong? What did you expect to happen?',
    idea: 'What would make Larkwell better for you?',
    praise: "What's working well for you?",
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <MessageSquareHeart className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} /> Send feedback
          </DialogTitle>
          <DialogDescription>
            Report a bug, request a feature, or tell us what's working. Even a sentence helps.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          {/* Type selector */}
          <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Feedback type">
            {TYPES.map((t) => {
              const active = type === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setType(t.id)}
                  aria-pressed={active}
                  className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-2.5 text-xs font-500 transition-colors ${active ? 'border-[hsl(var(--primary))] text-[hsl(var(--primary))]' : 'border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))]'}`}
                  style={active ? { background: 'hsl(var(--primary) / 0.08)' } : {}}
                  data-testid={`feedback-type-${t.id}`}
                >
                  <t.icon className="h-4 w-4" /> {t.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-1.5" role="radiogroup" aria-label="Rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(rating === n ? 0 : n)}
                aria-label={`${n} star${n > 1 ? 's' : ''}`}
                aria-pressed={rating >= n}
                className="p-1 rounded-md transition-transform hover:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                style={{ outlineColor: 'hsl(var(--primary))' }}
                data-testid={`feedback-star-${n}`}
              >
                <Star
                  className="h-6 w-6 transition-colors"
                  style={{
                    color: rating >= n ? 'hsl(var(--brand-honey))' : 'hsl(var(--muted-foreground))',
                    fill: rating >= n ? 'hsl(var(--brand-honey))' : 'transparent',
                  }}
                />
              </button>
            ))}
          </div>

          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={placeholders[type]}
            className="min-h-28 rounded-xl resize-none"
            data-testid="feedback-message-input"
          />

          <label className="flex items-start gap-3 rounded-xl border border-[hsl(var(--border))] p-3 cursor-pointer">
            <Switch checked={allowFeature} onCheckedChange={setAllowFeature} data-testid="feedback-allow-feature" />
            <span className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
              You can share this as a quote (with my first name) on the Larkwell site.
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" className="rounded-xl" onClick={() => onOpenChange(false)} disabled={busy} data-testid="feedback-cancel">Cancel</Button>
          <Button className="rounded-xl gap-1.5" style={{ background: 'hsl(var(--primary))' }} onClick={submit} disabled={busy} data-testid="feedback-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
